import type { ReactNode } from "react";
import { X } from "lucide-react";
import { Icon } from "./Icon";

export interface DialogProps {
  open?: boolean;
  title?: string;
  children?: ReactNode;
  /** Action row, right-aligned on a sunken bar. */
  footer?: ReactNode;
  onClose?: () => void;
  width?: number;
}

/**
 * Modal for confirmations and short forms. Fixed over the viewport: the prototype's absolute
 * positioning suits its framed screens, but here a long page would put the dialog out of view.
 */
export function Dialog({ open = true, title, children, footer, onClose, width = 520 }: DialogProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(28,63,40,.45)] p-7" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
        style={{ width }}
        className="max-w-full overflow-hidden rounded-xl bg-surface-card shadow-xl"
      >
        <div className="flex items-center justify-between gap-5 border-b border-border-subtle px-7 py-6">
          <h3 className="mb-0 type-h4">{title}</h3>
          {onClose && (
            <button type="button" aria-label="Close" onClick={onClose} className="grid size-8 cursor-pointer place-items-center rounded-sm text-ink-600 hover:bg-paper-2">
              <Icon icon={X} size={18} />
            </button>
          )}
        </div>
        <div className="p-7 type-body text-text-body">{children}</div>
        {footer && <div className="flex justify-end gap-4 border-t border-border-subtle bg-surface-sunken px-7 py-6">{footer}</div>}
      </div>
    </div>
  );
}
